import {
  claimConversation,
  closeConversation,
  sendAgentMessage,
  transferConversation,
  updateNotes,
} from '@/services/webchat-service/webchat.service';
import { openNotification } from '@/utils/notification/notification';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { t } from 'i18next';

// Surface the server-provided error reason (e.g. "Call already claimed",
// "Call ended", "No Twilio call_sid") instead of a generic toast. The
// claim controller returns 422 with `{ error: "..." }` from
// LiveCalls::ClaimService::NotClaimable.message; other endpoints may
// use `{ errors: [...] }`. Falls back to the generic string when
// nothing recognizable is present.
function extractServerMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; errors?: string | string[] } | undefined;
    if (data?.error) return data.error;
    if (Array.isArray(data?.errors) && data.errors.length) return data.errors[0];
    if (typeof data?.errors === 'string' && data.errors.length) return data.errors;
  }
  return fallback;
}

export const useWebchatActions = () => {
  const queryClient = useQueryClient();

  const invalidateWebchat = () => {
    queryClient.invalidateQueries({ queryKey: ReactQueryKeys.WEBCHAT_CONVERSATIONS() });
    queryClient.invalidateQueries({ queryKey: ReactQueryKeys.WEBCHAT_QUEUE_STATS() });
  };

  const { mutateAsync: claim, isPending: isClaiming } = useMutation({
    mutationFn: ({ id, translate, agentLanguage }: { id: number; translate: boolean; agentLanguage?: string }) =>
      claimConversation(id, translate, agentLanguage),
    onSuccess: () => {
      invalidateWebchat();
      openNotification({ message: t('webchat:actions.claimed'), type: 'success' });
    },
    onError: (err) => {
      // Concurrent-claim race ("Call already claimed") and other
      // NotClaimable reasons get the server's text instead of a
      // generic "Failed to claim" — the rep needs to know *why*.
      openNotification({
        message: extractServerMessage(err, t('webchat:actions.claimFailed')),
        type: 'error',
      });
    },
  });

  const { mutateAsync: close, isPending: isClosing } = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => closeConversation(id, reason),
    onSuccess: () => {
      invalidateWebchat();
      openNotification({ message: t('webchat:actions.closed'), type: 'success' });
    },
    onError: (err) => {
      openNotification({
        message: extractServerMessage(err, t('webchat:actions.closeFailed')),
        type: 'error',
      });
    },
  });

  const { mutateAsync: transfer, isPending: isTransferring } = useMutation({
    mutationFn: ({ id, agentId, notes }: { id: number; agentId: number; notes?: string }) =>
      transferConversation(id, agentId, notes),
    onSuccess: () => {
      invalidateWebchat();
      openNotification({ message: t('webchat:actions.transferred'), type: 'success' });
    },
    onError: (err) => {
      openNotification({
        message: extractServerMessage(err, t('webchat:actions.transferFailed')),
        type: 'error',
      });
    },
  });

  const { mutateAsync: sendMessage, isPending: isSending } = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) => sendAgentMessage(id, text),
    onError: (err) => {
      openNotification({
        message: extractServerMessage(err, t('webchat:actions.sendFailed')),
        type: 'error',
      });
    },
  });

  const { mutateAsync: saveNotes } = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => updateNotes(id, notes),
    onSuccess: () => {
      openNotification({ message: t('webchat:actions.notesSaved'), type: 'success' });
    },
    onError: (err) => {
      openNotification({
        message: extractServerMessage(err, t('webchat:actions.notesFailed')),
        type: 'error',
      });
    },
  });

  return {
    claim,
    close,
    transfer,
    sendMessage,
    saveNotes,
    isClaiming,
    isClosing,
    isTransferring,
    isSending,
  };
};
